import { useSelector } from "react-redux";

// Types
import { ILocationState } from "@/Common/interfaces/location";

// MUI
import { Skeleton, Stack, Typography } from "@mui/material";

// Images
import emptyTable from "@/assets/images/commons/empty_table.png";

// Helpers
import { and } from "@/Common/helpers/sonar";

// Layouts
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";

// Components
import TitlingTable from "./TitlingTable";

// Hooks
import { useTitlingQuery } from "./hooks/useTitlingQuery";
import { useTitlingUpdateSubscription } from "./hooks/useTitlingUpdateSubscription";
import { ITitling } from "@/DataPages/types/titling/model";

interface TitlingLayerProps {
	periodTreeSelection: IPeriodTreeSelection;
	isTabSelected: boolean;
	hasPermission: boolean;
}

export default function TitlingBody(props: Readonly<TitlingLayerProps>) {
	const { periodTreeSelection, isTabSelected, hasPermission } = props;

	const commissionCycleId = periodTreeSelection?.cycleId ?? "";
	const payPeriodId = periodTreeSelection?.payPeriodId ?? "";
	const locationId = useSelector((state: ILocationState) => state.location.location?.locationId);

	const {
		titlingData,
		isPaginating,
		isLoading: isLoadingTitling,
	} = useTitlingQuery({
		locationId,
		commissionCycleId,
		payPeriodId,
		enabled: isTabSelected,
		hasPermission,
	});

	useTitlingUpdateSubscription({
		locationId,
		commissionCycleId,
		payPeriodId,
	});

	const hasTitlingData = !!titlingData?.length;
	const noDataLoaded = and(!isLoadingTitling, !hasTitlingData);
	const isLoading = isLoadingTitling;

	return (
		<Stack className="py-8" height={690}>
			<Header title={periodTreeSelection?.titleExtended} />
			{
				<>
					{noDataLoaded && <EmptyState />}

					<Stack className="pt-4 !h-full">
						{isLoading ? (
							<TableLoader toolbar buttons={2} content rows={5} multiTitles={8} />
						) : (
							hasTitlingData && <TitlingTable isPaginating={isPaginating} data={titlingData as ITitling[]} />
						)}
					</Stack>
				</>
			}
		</Stack>
	);
}

// MARK:- Supporting Components
function Header(props: { title?: string | null }) {
	const { title } = props;

	if (!title) {
		return <Skeleton variant="rectangular" height={30} width={350} className={"rounded-[6px] mb-[15px]"} />;
	}
	return <Typography className={"page-title"}>{`${title} Titling`}</Typography>;
}

function EmptyState() {
	return <EmptyBody image={emptyTable} title={"No Titling Data for this period"} bodyHeightClassName={"!h-[380px]"} />;
}
